/**
 * Pointy-top hex grid in axial coordinates (q, r).
 * Algorithms follow Red Blob Games: https://www.redblobgames.com/grids/hexagons/
 */
export interface Hex {
  readonly q: number;
  readonly r: number;
}

/** `+ 0` turns -0 into 0 so keys and equality stay stable. */
export const hex = (q: number, r: number): Hex => ({ q: q + 0, r: r + 0 });
export const ORIGIN: Hex = hex(0, 0);

export const hexKey = (h: Hex): string => `${h.q},${h.r}`;

export function parseHexKey(key: string): Hex {
  const [q, r] = key.split(',').map(Number);
  if (q === undefined || r === undefined || Number.isNaN(q) || Number.isNaN(r)) {
    throw new Error(`Bad hex key: ${key}`);
  }
  return hex(q, r);
}

export const hexEquals = (a: Hex, b: Hex): boolean => a.q === b.q && a.r === b.r;
export const hexAdd = (a: Hex, b: Hex): Hex => hex(a.q + b.q, a.r + b.r);
export const hexSub = (a: Hex, b: Hex): Hex => hex(a.q - b.q, a.r - b.r);
export const hexScale = (a: Hex, k: number): Hex => hex(a.q * k, a.r * k);

export function hexDistance(a: Hex, b: Hex): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
}

/** The six neighbour directions, counter-clockwise starting east. */
export const HEX_DIRECTIONS: readonly Hex[] = [
  hex(1, 0),
  hex(1, -1),
  hex(0, -1),
  hex(-1, 0),
  hex(-1, 1),
  hex(0, 1),
];

export function hexNeighbors(h: Hex): Hex[] {
  return HEX_DIRECTIONS.map((d) => hexAdd(h, d));
}

/** All hexes at exactly `radius` steps from `center` (6·radius of them). */
export function hexRing(center: Hex, radius: number): Hex[] {
  if (radius === 0) return [center];
  const result: Hex[] = [];
  let current = hexAdd(center, hexScale(HEX_DIRECTIONS[4] as Hex, radius));
  for (let side = 0; side < 6; side++) {
    for (let step = 0; step < radius; step++) {
      result.push(current);
      current = hexAdd(current, HEX_DIRECTIONS[side] as Hex);
    }
  }
  return result;
}

/** All hexes within `radius` of `center`, centre first, ring by ring. */
export function hexSpiral(center: Hex, radius: number): Hex[] {
  const result: Hex[] = [];
  for (let k = 0; k <= radius; k++) result.push(...hexRing(center, k));
  return result;
}

/** Centre of a hex in world units for hexes of circumradius `size`. */
export function hexToPixel(h: Hex, size = 1): { x: number; y: number } {
  return {
    x: size * Math.sqrt(3) * (h.q + h.r / 2),
    y: size * 1.5 * h.r,
  };
}

/** Round fractional axial coordinates to the nearest hex (cube rounding). */
export function hexRound(q: number, r: number): Hex {
  const s = -q - r;
  let rq = Math.round(q);
  let rr = Math.round(r);
  const rs = Math.round(s);
  const dq = Math.abs(rq - q);
  const dr = Math.abs(rr - r);
  const ds = Math.abs(rs - s);
  if (dq > dr && dq > ds) rq = -rr - rs;
  else if (dr > ds) rr = -rq - rs;
  return hex(rq, rr);
}

export function pixelToHex(x: number, y: number, size = 1): Hex {
  const q = ((Math.sqrt(3) / 3) * x - (1 / 3) * y) / size;
  const r = ((2 / 3) * y) / size;
  return hexRound(q, r);
}

/** Polar angle of a hex centre around the origin, in [0, 2π). */
export function hexAngle(h: Hex): number {
  const { x, y } = hexToPixel(h);
  const a = Math.atan2(y, x);
  return a < 0 ? a + 2 * Math.PI : a;
}

/** Smallest absolute difference between two angles, in [0, π]. */
export function angleDelta(a: number, b: number): number {
  const d = Math.abs(a - b) % (2 * Math.PI);
  return d > Math.PI ? 2 * Math.PI - d : d;
}

/** The hex on the ring of `radius` around the origin closest to `angle`. */
export function hexOnRingAt(radius: number, angle: number): Hex {
  let best = ORIGIN;
  let bestDelta = Infinity;
  for (const h of hexRing(ORIGIN, radius)) {
    const delta = angleDelta(hexAngle(h), angle);
    if (delta < bestDelta) {
      best = h;
      bestDelta = delta;
    }
  }
  return best;
}
